import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Save, Check, Shield, Phone, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { user, updateUser, login } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [region, setRegion] = useState(user?.region || 'Alberta');
  const [phone, setPhone] = useState(user?.phone || '');
  const [disciplines, setDisciplines] = useState(user?.disciplines || []);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSuccess, setAdminSuccess] = useState(false);
  const [adminError, setAdminError] = useState('');

  const allDisciplines = ['Heading', 'Heeling', 'Breakaway', 'Ranch Roping'];
  
  const regions = [
    'Alberta',
    'British Columbia', 
    'Saskatchewan',
    'Manitoba',
    'Ontario',
    'Quebec',
    'Nova Scotia',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Prince Edward Island',
    'Northwest Territories',
    'Yukon',
    'Nunavut'
  ];

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setRegion(user.region || 'Alberta');
      setPhone(user.phone || '');
      setDisciplines(user.disciplines || []);
    }
  }, [user]);

  const handleDisciplineToggle = (discipline) => {
    setDisciplines(prev => 
      prev.includes(discipline)
        ? prev.filter(d => d !== discipline)
        : [...prev, discipline]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const response = await axios.put(`${API_URL}/api/profile`, {
        name,
        region,
        phone,
        disciplines
      });
      updateUser(response.data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Link to="/dashboard" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <h1 className="font-display font-bold text-3xl uppercase tracking-tight">
            Profile & Settings
          </h1>
        </div>

        {/* Account Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-display text-lg uppercase">Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium capitalize">{user?.role}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Elevated Credits</span>
              <span className="font-medium">{user?.credits || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Subscription</span>
              <span className="font-medium capitalize">{user?.subscription_status || 'None'}</span>
            </div>
            {user?.subscription_end && (
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Subscription Ends</span>
                <span className="font-medium">{formatDate(user.subscription_end)}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-2">
              <span className="text-muted-foreground">Member Since</span>
              <span className="font-medium">{formatDate(user?.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Subscription CTA for trial/inactive users */}
        {(user?.subscription_status === 'trial' || !user?.subscription_status || user?.subscription_status === 'inactive') && (
          <Card className="mb-6 border-primary bg-gradient-to-r from-primary/10 to-secondary/10">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-display font-bold text-lg uppercase flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    {user?.subscription_status === 'trial' ? 'Upgrade Your Account' : 'Get Full Access'}
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Subscribe to unlock community, AI assistant, video library, marketplace, and more.
                  </p>
                  <ul className="mt-3 space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-secondary" />
                      <span>Full community access</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-secondary" />
                      <span>AI Training Assistant</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-secondary" />
                      <span>Free video library</span>
                    </li>
                  </ul>
                </div>
                <Link to="/subscription" className="flex-shrink-0">
                  <Button size="lg" className="w-full sm:w-auto rounded-full font-display uppercase" data-testid="profile-subscribe-btn">
                    View Plans
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-lg uppercase">Profile</CardTitle>
            <CardDescription>Update your profile information</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {success && (
              <Alert className="mb-6 bg-secondary/10 border-secondary">
                <Check className="w-4 h-4 text-secondary" />
                <AlertDescription className="text-secondary">Profile updated successfully!</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Region</Label>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger className="h-12" data-testid="profile-region-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number (for SMS notifications)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="h-12"
                  data-testid="profile-phone-input"
                />
                <p className="text-xs text-muted-foreground">
                  Get notified by text when your coach responds to a critique
                </p>
              </div>

              <div className="space-y-3">
                <Label>Disciplines</Label>
                <div className="grid grid-cols-2 gap-3">
                  {allDisciplines.map(d => (
                    <div
                      key={d}
                      className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        disciplines.includes(d) ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handleDisciplineToggle(d)}
                    >
                      <Checkbox
                        checked={disciplines.includes(d)}
                        onCheckedChange={() => handleDisciplineToggle(d)}
                      />
                      <span className="font-medium">{d}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold uppercase tracking-wide"
                data-testid="save-profile-btn"
              >
                {loading ? 'Saving...' : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Subscription Link */}
        <div className="mt-6 text-center">
          <Link to="/subscription" className="text-primary hover:underline font-medium">
            Manage Subscription & Elevated Credits →
          </Link>
        </div>

        {/* Cancel Subscription */}
        {(user?.subscription_status === 'active' || user?.subscription_status === 'trial') && (
          <Card className="mt-6 border-red-500/20">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-display font-bold uppercase text-sm">Manage Subscription</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Update your card, view invoices, or cancel anytime. Cancellation takes effect at the end of your current billing period.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API_URL}/api/subscriptions/portal`);
                        window.location.href = res.data.url;
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Could not open billing portal');
                      }
                    }}
                    data-testid="open-billing-portal-btn"
                  >
                    Manage Billing
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-full border-red-500/30 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={async () => {
                      if (!window.confirm('Cancel your subscription? You will keep access until the end of your current billing period.')) return;
                      try {
                        const res = await axios.post(`${API_URL}/api/subscriptions/cancel`);
                        toast.success(res.data.message);
                        setTimeout(() => window.location.reload(), 1800);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
                      }
                    }}
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Subscription
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {user?.subscription_status === 'cancelled' && user?.subscription_end && (
          <Card className="mt-6 border-amber-500/20 bg-amber-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-amber-700">
                Your subscription has been cancelled. You have access until <strong>{new Date(user.subscription_end).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                <Link to="/subscription" className="text-primary hover:underline ml-1">Resubscribe →</Link>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Admin Setup - only show for non-admin users */}
        {user?.role !== 'admin' && (
          <Card className="mt-6 border-dashed">
            <CardHeader>
              <CardTitle className="font-display text-lg uppercase flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Admin Setup
              </CardTitle>
              <CardDescription>Enter the admin setup key to become an administrator</CardDescription>
            </CardHeader>
            <CardContent>
              {adminError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{adminError}</AlertDescription>
                </Alert>
              )}
              {adminSuccess && (
                <Alert className="mb-4 bg-secondary/10 border-secondary">
                  <Check className="w-4 h-4 text-secondary" />
                  <AlertDescription className="text-secondary">You are now an admin! Refreshing...</AlertDescription>
                </Alert>
              )}
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    type={showAdminKey ? "text" : "password"}
                    placeholder="Enter admin setup key"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                    data-testid="admin-key-input"
                  />
                  <button type="button" onClick={() => setShowAdminKey(!showAdminKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showAdminKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={async () => {
                    setAdminError('');
                    setAdminLoading(true);
                    try {
                      const response = await axios.post(`${API_URL}/api/auth/make-admin`, { setup_key: adminKey });
                      localStorage.setItem('token', response.data.token);
                      setAdminSuccess(true);
                      setTimeout(() => window.location.reload(), 1500);
                    } catch (err) {
                      setAdminError(err.response?.data?.detail || 'Invalid setup key');
                    } finally {
                      setAdminLoading(false);
                    }
                  }}
                  disabled={adminLoading || !adminKey}
                  data-testid="make-admin-btn"
                >
                  {adminLoading ? 'Verifying...' : 'Become Admin'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
